import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

/** Redirect /{from}/... → /{to}/... keeping the sub-path and query (legacy portal URLs). */
const PrefixRedirect = ({ from, to }) => {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.pathname.slice(from.length)}${loc.search}`} replace />;
};
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import RegistrationGate from "./components/RegistrationGate";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import { RouteSeoDefaults } from "./seo/SeoHead";
import { AIFormCheckGate } from "./components/FeatureUnavailable";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { Toaster } from "./components/ui/sonner";

// PublicLanding is the primary landing route ("/") — keep it static so the
// homepage hero renders on the same tick as the initial JS bundle. Every
// other route is code-split via React.lazy so the initial payload stays lean.
import PublicLanding from "./pages/PublicLanding";

import "./App.css";

// --- Route-level lazy imports ---
// Auth Pages
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));

// New dedicated admin/coach login + admin portal (Feb 2026 redesign)
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const CoachLogin = lazy(() => import("./pages/CoachLogin"));
const CoachForgotPassword = lazy(() => import("./pages/CoachForgotPassword"));
const CoachResetPassword = lazy(() => import("./pages/CoachResetPassword"));
const AdminChangePassword = lazy(() => import("./pages/AdminChangePassword"));
const AdminAccessDenied = lazy(() => import("./pages/AdminAccessDenied"));
const AdminAcceptInvite = lazy(() => import("./pages/AdminAcceptInvite"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminAccountSecurity = lazy(() => import("./pages/AdminAccountSecurity"));
const AdminInvitations = lazy(() => import("./pages/AdminInvitations"));
const AdminInstallments = lazy(() => import("./pages/AdminInstallments"));
const AdminDashboardV2 = lazy(() => import("./pages/AdminDashboardV2"));
const AdminClassArea = lazy(() => import("./pages/AdminClassArea"));
const AdminStudentArea = lazy(() => import("./pages/AdminStudentArea"));
const AdminPaymentsArea = lazy(() => import("./pages/AdminPaymentsArea"));
const AdminCoachArea = lazy(() => import("./pages/AdminCoachArea"));
const AdminWebsiteArea = lazy(() => import("./pages/AdminWebsiteArea"));
const AdminEvents = lazy(() => import("./pages/events/AdminEvents"));
const LiveSessions = lazy(() => import("./pages/live/LiveSessions"));
const MyClassesRecordings = lazy(() => import("./pages/live/MyClassesRecordings"));
const AdminLiveSessions = lazy(() => import("./pages/live/AdminLiveSessions"));
const AdminEventShell = lazy(() => import("./pages/events/AdminEventShell"));
const PriceLockOffers = lazy(() => import("./pages/pricelock/PriceLockOffers"));
const PriceLockReserve = lazy(() => import("./pages/pricelock/PriceLockReserve"));
const MyReservations = lazy(() => import("./pages/pricelock/MyReservations"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ClassesManagement = lazy(() => import("./pages/ClassesManagement"));
const StudentsManagement = lazy(() => import("./pages/StudentsManagement"));
const AdminStudentDetail = lazy(() => import("./pages/AdminStudentDetail"));
const AdminPaymentVerify = lazy(() => import("./pages/AdminPaymentVerify"));
const CoachesManagement = lazy(() => import("./pages/CoachesManagement"));
const AdminOffDayQueue = lazy(() => import("./pages/AdminOffDayQueue"));
const AdminQuotationRequests = lazy(
  () => import("./pages/AdminQuotationRequests"),
);
const AdminOnlineTrainingRequests = lazy(
  () => import("./pages/AdminOnlineTrainingRequests"),
);
const AdminChangeRequests = lazy(() => import("./pages/AdminChangeRequests"));
const AdminWaitlists = lazy(() => import("./pages/AdminWaitlists"));
const AdminSubscriptionSettings = lazy(
  () => import("./pages/AdminSubscriptionSettings"),
);
const AdminTermsEditor = lazy(() => import("./pages/AdminTermsEditor"));
const AdminSiteContent = lazy(() => import("./pages/AdminSiteContent"));
const EnhancementScheduleAdmin = lazy(
  () => import("./pages/EnhancementScheduleAdmin"),
);
const ClassDetail = lazy(() => import("./pages/ClassDetail"));
const PackagesManagement = lazy(() => import("./pages/PackagesManagement"));
const Reports = lazy(() => import("./pages/Reports"));

// Coach Pages
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));
const MyCoachProfile = lazy(() => import("./pages/MyCoachProfile"));
const MyCoachAvailability = lazy(() => import("./pages/MyCoachAvailability"));
const MyCoachesInPortal = lazy(() => import("./pages/MyCoachesInPortal"));
const AdminCoachMigration = lazy(() => import("./pages/AdminCoachMigration"));
const AdminAIFormCheckKB = lazy(() => import("./pages/AdminAIFormCheckKB"));
const AdminAIFormCheckSettings = lazy(() => import("./pages/AdminAIFormCheckSettings"));
const MyClasses = lazy(() => import("./pages/MyClasses"));
const CoachClassDetail = lazy(() => import("./pages/CoachClassDetail"));
const CoachMarkAttendance = lazy(() => import("./pages/CoachMarkAttendance"));
const CoachScanCheckin = lazy(() => import("./pages/CoachScanCheckin"));
const CoachCheckinPortal = lazy(() => import("./pages/CoachCheckinPortal"));
const MagicCoachLogin = lazy(() => import("./pages/MagicCoachLogin"));
const MarkAttendance = lazy(() => import("./pages/MarkAttendance"));

// AI Form Check — admin review workflow (Phase B, Msg 224b, Feb 2026)
const AIReviewQueue = lazy(() =>
  import("./pages/AIReviewReports").then((m) => ({ default: m.AIReviewQueue })),
);
const AIReviewDetail = lazy(() =>
  import("./pages/AIReviewReports").then((m) => ({
    default: m.AIReviewDetail,
  })),
);

// Student Pages
const Schedule = lazy(() => import("./pages/Schedule"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const MyRaces = lazy(() => import("./pages/MyRaces"));
const BuyPackages = lazy(() => import("./pages/BuyPackages"));
const WaitlistConfirm = lazy(() => import("./pages/WaitlistConfirm"));
const WalkInClasses = lazy(() => import("./pages/WalkInClasses"));
const Trials = lazy(() => import("./pages/Trials"));
const PrivateBooking = lazy(() => import("./pages/PrivateBooking"));
const AdminPrivateBookings = lazy(() => import("./pages/AdminPrivateBookings"));
const CoachPrivateBookings = lazy(() => import("./pages/CoachPrivateBookings"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const ManualPayment = lazy(() => import("./pages/ManualPayment"));
const PaymentSubmitted = lazy(() => import("./pages/PaymentSubmitted"));
const BookingSummaryPublic = lazy(() => import("./pages/BookingSummaryPublic"));
const AdminManualPayments = lazy(() => import("./pages/AdminManualPayments"));
const AdminPaymentSettings = lazy(() => import("./pages/AdminPaymentSettings"));
const AdminEmailSettings = lazy(() => import("./pages/AdminEmailSettings"));
const AdminLocationImages = lazy(() => import("./pages/AdminLocationImages"));
const PrivateClass = lazy(() => import("./pages/PrivateClass"));
const AllClasses = lazy(() => import("./pages/AllClasses"));
const UpcomingClasses = lazy(() => import("./pages/UpcomingClasses"));
const EventClassPage = lazy(() => import("./pages/EventClassPage"));
const AdminEventClasses = lazy(() => import("./pages/AdminEventClasses"));
const AdminEventManage = lazy(() => import("./pages/AdminEventManage"));
const SupplierLogin = lazy(() => import("./pages/supplier/SupplierLogin"));
const SupplierDashboard = lazy(() => import("./pages/supplier/SupplierDashboard"));
const SupplierEvent = lazy(() => import("./pages/supplier/SupplierEvent"));
const SupplierStock = lazy(() => import("./pages/supplier/SupplierStock"));
const AdminSuppliers = lazy(() => import("./pages/AdminSuppliers"));
const AdminSupplierDetail = lazy(() => import("./pages/AdminSupplierDetail"));
const RaceCalendar = lazy(() => import("./pages/RaceCalendar"));
const FindMyRace = lazy(() => import("./pages/FindMyRace"));
const AdminRaceCalendar = lazy(() => import("./pages/AdminRaceCalendar"));
const RunnerMarket = lazy(() => import("./pages/RunnerMarket"));
const RunnerMarketProduct = lazy(() => import("./pages/RunnerMarketProduct"));
const RunnerMarketCart = lazy(() => import("./pages/RunnerMarketCart"));
const RunnerMarketOrder = lazy(() => import("./pages/RunnerMarketOrder"));
const RunnerMarketCheckout = lazy(() => import("./pages/RunnerMarketCheckout"));
const RunnerMarketFindOrder = lazy(() => import("./pages/RunnerMarketFindOrder"));
const AdminRunnerMarket = lazy(() => import("./pages/AdminRunnerMarket"));
const AdminTrainingJourney = lazy(() => import("./pages/AdminTrainingJourney"));
const CartPage = lazy(() => import("./pages/CartPage"));
const GuestCheckout = lazy(() => import("./pages/GuestCheckout"));
const GuestConfirm = lazy(() => import("./pages/GuestConfirm"));
const Membership = lazy(() => import("./pages/Membership"));
const MembershipsAdmin = lazy(() => import("./pages/MembershipsAdmin"));
// PasswordReset.js exports two named components — split each into its own lazy chunk.
const ForgotPassword = lazy(() =>
  import("./pages/PasswordReset").then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() =>
  import("./pages/PasswordReset").then((m) => ({ default: m.ResetPassword })),
);
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const CompleteRegistration = lazy(() => import("./pages/CompleteRegistration"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const LinkAccount = lazy(() => import("./pages/LinkAccount"));
const ChangeCoachRequest = lazy(() => import("./pages/ChangeCoachRequest"));
const StudentInfo = lazy(() => import("./pages/StudentInfo"));
const PrivateEnquiries = lazy(() => import("./pages/PrivateEnquiries"));
const FindMyClass = lazy(() => import("./pages/FindMyClass"));
const FindMyFirstClass = lazy(() => import("./pages/FindMyFirstClass"));
const BookClass = lazy(() => import("./pages/BookClass"));
const BookClassConfirmation = lazy(() => import("./pages/BookClassConfirmation"));
const Coaches = lazy(() => import("./pages/Coaches"));
const CoachProfile = lazy(() => import("./pages/CoachProfile"));
const Checkout = lazy(() => import("./pages/CartCheckout"));
const FindClassSubmissions = lazy(() => import("./pages/FindClassSubmissions"));
const Profile = lazy(() => import("./pages/Profile"));
const MyPayments = lazy(() => import("./pages/MyPayments"));
const MyWallet = lazy(() => import("./pages/MyWallet"));
const PrivateAssessment = lazy(() => import("./pages/PrivateAssessment"));
const BalancePayment = lazy(() => import("./pages/BalancePayment"));
const OnlineTrainingPlan = lazy(() => import("./pages/OnlineTrainingPlan"));
const TrainingPlanReservation = lazy(() => import("./pages/TrainingPlanReservation"));
const RunningReportPage = lazy(() => import("./pages/RunningReportPage"));
const ServicePage = lazy(() => import("./pages/services/ServicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AIRunningFormCheck = lazy(() => import("./pages/AIRunningFormCheck"));
const AIFormCheckSession = lazy(() => import("./pages/AIFormCheckSession"));
const AIFormCheckVideo = lazy(() => import("./pages/AIFormCheckVideo"));
const AdminAIFormCheckTest = lazy(() => import("./pages/AdminAIFormCheckTest"));
const AdminAIFormCheckQueue = lazy(
  () => import("./pages/AdminAIFormCheckQueue"),
);
const MyAIFormChecks = lazy(() => import("./pages/MyAIFormChecks"));
const TechniqueAssessment = lazy(() => import("./pages/TechniqueAssessment"));
const TechniqueAssessmentStatus = lazy(() => import("./pages/TechniqueAssessmentStatus"));
const MyTechniqueAssessments = lazy(() => import("./pages/MyTechniqueAssessments"));
const AdminTechniqueAssessments = lazy(() => import("./pages/AdminTechniqueAssessments"));
const MallLongRun = lazy(() => import("./pages/MallLongRun"));
const MallLongRunRegistration = lazy(() => import("./pages/MallLongRunRegistration"));
const MallLongRunAdmin = lazy(() => import("./pages/MallLongRunAdmin"));
const AdminWhatsAppReminders = lazy(() => import("./pages/AdminWhatsAppReminders"));
const PersonalCoachVideoReview = lazy(() => import("./pages/PersonalCoachVideoReview"));
const MascotConcepts = lazy(() => import("./pages/MascotConcepts"));
const CompareTrainingOptions = lazy(
  () => import("./pages/CompareTrainingOptions"),
);
const Workshops = lazy(() => import("./pages/Workshops"));
const RunnersStories = lazy(() => import("./pages/RunnersStories"));
const PrivateCoachingPackages = lazy(
  () => import("./pages/PrivateCoachingPackages"),
);
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const FamilyProfiles = lazy(() => import("./pages/FamilyProfiles"));
const AdminFamilyProfiles = lazy(() => import("./pages/AdminFamilyProfiles"));
const AdminAIImageStudio = lazy(() => import("./pages/AdminAIImageStudio"));
const AIImageStudio = lazy(() => import("./pages/AIImageStudio"));
const MyPromotions = lazy(() => import("./pages/MyPromotions"));
const AdminBulkImport = lazy(() => import("./pages/AdminBulkImport"));
const AdminTestUserCleanup = lazy(() => import("./pages/AdminTestUserCleanup"));
const MyMembership = lazy(() => import("./pages/MyMembership"));
const BookingHelp = lazy(() => import("./pages/BookingHelp"));
const CancellationRefundPolicy = lazy(
  () => import("./pages/CancellationRefundPolicy"),
);
const BookingRulesPolicy = lazy(() => import("./pages/BookingRulesPolicy"));
const AdminReferralCodes = lazy(() => import("./pages/AdminReferralCodes"));
const AdminPromotions = lazy(() => import("./pages/AdminPromotions"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminPromoCampaigns = lazy(() => import("./pages/AdminPromoCampaigns"));
const AdminBookingResolutions = lazy(
  () => import("./pages/AdminBookingResolutions"),
);
const AdminAIKnowledge = lazy(() => import("./pages/AdminAIKnowledge"));
const RefundRequestsAdmin = lazy(() => import("./pages/RefundRequestsAdmin"));

// AskLGRAWidget is a floating chat trigger — deferred so the homepage LCP
// isn't blocked by the chat library. Suspense fallback is null so nothing
// renders until the chunk finishes.
const AskLGRAWidget = lazy(() => import("./components/AskLGRAWidget"));

// Persistent mobile bottom nav (Feb 2026) — 4 highest-frequency customer
// actions. Lazy-loaded so the marketing homepage never pays the cost for
// signed-out visitors.
const MobileBottomNav = lazy(() => import("./components/MobileBottomNav"));

// ReturnToBookingBanner (Aug 2026) — post-payment "Book Now / Book Later"
// prompt that shows only when the customer entered from /private-booking,
// completed a Private Coaching Hours purchase, and lands back on any other
// page. Reads sessionStorage flag primed by /private-coaching-packages, so
// unrelated purchases never trigger it and dismissal is permanent per session.
const ReturnToBookingBanner = lazy(
  () => import("./components/ReturnToBookingBanner"),
);

// Lightweight fallback shown briefly while a route chunk loads. Deliberately
// minimal so it never becomes the LCP element on slow networks.
const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"
    data-testid="route-suspense-fallback"
    role="status"
    aria-live="polite"
  >
    <div className="text-[#7E22CE] font-black tracking-widest text-xs uppercase animate-pulse">
      Loading…
    </div>
  </div>
);

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) return null;

  if (user.role === "admin") {
    // Feb 2026: admins now live under /admin/*. This legacy /dashboard
    // route is kept only so old bookmarks still land somewhere sane.
    return <Navigate to="/admin/dashboard" replace />;
  } else if (user.role === "coach") {
    return <CoachDashboard />;
  } else {
    // Students no longer have a portal — send them to the public website.
    return <Navigate to="/" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ImpersonationBanner />
          <RegistrationGate>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              {/* Legacy /signup, /sign-up, /student/register, /student/signup
                aliases — always route to the new unified /register (Create
                Account mode of AuthCard). `replace` avoids polluting
                browser history so Back doesn't hit the outdated URL. */}
              <Route
                path="/signup"
                element={<Navigate to="/register" replace />}
              />
              <Route
                path="/sign-up"
                element={<Navigate to="/register" replace />}
              />
              <Route
                path="/student/register"
                element={<Navigate to="/register" replace />}
              />
              <Route
                path="/student/signup"
                element={<Navigate to="/register" replace />}
              />
              <Route
                path="/student-portal"
                element={<Navigate to="/" replace />}
              />
              <Route
                path="/student-dashboard"
                element={<Navigate to="/" replace />}
              />

              {/* ─────── LGRA Admin Portal (Feb 2026 redesign) ───────
                Dedicated /admin/* URL space, separate from the customer
                login and coach portal. */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route
                path="/admin/login"
                element={<Navigate to="/admin" replace />}
              />
              <Route
                path="/admin/access-denied"
                element={<AdminAccessDenied />}
              />
              <Route
                path="/admin/accept-invite/:token"
                element={<AdminAcceptInvite />}
              />
              <Route
                path="/admin/forgot-password"
                element={<AdminForgotPassword />}
              />
              <Route
                path="/admin/reset-password/:token"
                element={<AdminResetPassword />}
              />
              <Route
                path="/admin/change-password"
                element={
                  <ProtectedAdminRoute>
                    <AdminChangePassword />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/account/security"
                element={
                  <ProtectedAdminRoute>
                    <AdminAccountSecurity />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedAdminRoute>
                    <AdminDashboardV2 />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/invitations"
                element={
                  <ProtectedAdminRoute>
                    <AdminInvitations />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/installments"
                element={
                  <ProtectedAdminRoute>
                    <AdminInstallments />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/classes"
                element={
                  <ProtectedAdminRoute>
                    <AdminClassArea />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/students"
                element={
                  <ProtectedAdminRoute>
                    <AdminStudentArea />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/events"
                element={
                  <ProtectedAdminRoute>
                    <AdminEvents />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/live-sessions"
                element={
                  <ProtectedAdminRoute>
                    <AdminLiveSessions />
                  </ProtectedAdminRoute>
                }
              />
              <Route path="/LiveSession" element={<LiveSessions />} />
              <Route path="/livesession" element={<LiveSessions />} />
              <Route path="/live-sessions" element={<LiveSessions />} />
              <Route path="/live-sessions/:slug" element={<LiveSessions />} />
              <Route
                path="/my-classes-recordings"
                element={
                  <ProtectedRoute>
                    <MyClassesRecordings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/events/:eid"
                element={
                  <ProtectedAdminRoute>
                    <AdminEventShell />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <ProtectedAdminRoute>
                    <AdminPaymentsArea />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/payments/:mpId/verify"
                element={
                  <ProtectedAdminRoute>
                    <AdminPaymentVerify />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/students/:id"
                element={
                  <ProtectedAdminRoute>
                    <AdminStudentDetail />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/website"
                element={
                  <ProtectedAdminRoute>
                    <AdminWebsiteArea />
                  </ProtectedAdminRoute>
                }
              />
              {/* Coaches Area — keep /admin/coaches for the redesigned area
                landing page. The legacy CoachesManagement page is still
                reachable inside the area as the default tab. */}
              <Route
                path="/admin/coaches"
                element={
                  <ProtectedAdminRoute>
                    <AdminCoachArea />
                  </ProtectedAdminRoute>
                }
              />

              {/* Dedicated coach login */}
              <Route path="/coach" element={<CoachLogin />} />
              <Route
                path="/coach/login"
                element={<Navigate to="/coach" replace />}
              />
              <Route path="/coach/magic-login" element={<MagicCoachLogin />} />
              {/* Msg 226 (Feb 2026) — Coach Portal password reset */}
              <Route
                path="/coach/forgot-password"
                element={<CoachForgotPassword />}
              />
              <Route
                path="/coach/reset-password/:token"
                element={<CoachResetPassword />}
              />

              {/* Internal portals (admin + coach). Students never see a sidebar
                layout — their account pages use the public header instead. */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["admin", "coach"]}>
                    <DashboardRouter />
                  </ProtectedRoute>
                }
              />

              {/* Backward-compat redirects for legacy admin URLs → new
                /admin/* structure. Users who bookmarked the old URLs land
                on the correct new page without a broken link. */}
              <Route
                path="/classes"
                element={<Navigate to="/admin/classes" replace />}
              />
              <Route
                path="/students"
                element={<Navigate to="/admin/students" replace />}
              />
              {/* Legacy /admin/* single-page routes redirect into their new
                Area tabs. Deep-link to a specific tab via ?tab= param. */}
              <Route
                path="/admin/off-days"
                element={<Navigate to="/admin/coaches?tab=off-days" replace />}
              />
              <Route
                path="/admin/enhancement-schedule"
                element={
                  <Navigate to="/admin/classes?tab=enhancement" replace />
                }
              />
              <Route
                path="/admin/waitlists"
                element={<Navigate to="/admin/classes?tab=waitlists" replace />}
              />
              <Route
                path="/admin/private-bookings"
                element={<Navigate to="/admin/classes?tab=private" replace />}
              />
              <Route
                path="/admin/quotation-requests"
                element={
                  <Navigate to="/admin/classes?tab=quotations" replace />
                }
              />
              <Route
                path="/admin/online-training-plan-requests"
                element={
                  <Navigate to="/admin/classes?tab=online-plan" replace />
                }
              />
              <Route
                path="/admin/change-requests"
                element={
                  <Navigate to="/admin/coaches?tab=change-requests" replace />
                }
              />
              <Route
                path="/admin/family"
                element={<Navigate to="/admin/students?tab=family" replace />}
              />
              <Route
                path="/admin/packages"
                element={<Navigate to="/admin/students?tab=packages" replace />}
              />
              <Route
                path="/admin/memberships"
                element={
                  <Navigate to="/admin/students?tab=memberships" replace />
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <Navigate to="/admin/students?tab=subscriptions" replace />
                }
              />
              <Route
                path="/admin/booking-resolutions"
                element={
                  <Navigate to="/admin/students?tab=resolutions" replace />
                }
              />
              <Route
                path="/admin/refund-requests"
                element={<Navigate to="/admin/payments?tab=refunds" replace />}
              />
              <Route
                path="/admin/ai-review-reports"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AIReviewQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ai-review-reports/:id"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AIReviewDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payments/manual"
                element={<Navigate to="/admin/payments?tab=pending" replace />}
              />
              <Route
                path="/admin/referral-codes"
                element={
                  <Navigate to="/admin/students?tab=referrals" replace />
                }
              />
              <Route
                path="/admin/marketing"
                element={
                  <ProtectedAdminRoute>
                    <AdminMarketing />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/promotions"
                element={
                  <ProtectedAdminRoute>
                    <AdminPromotions />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/promo-campaigns"
                element={
                  <ProtectedAdminRoute>
                    <AdminPromoCampaigns />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/promo-campaigns/:cid"
                element={
                  <ProtectedAdminRoute>
                    <AdminPromoCampaigns />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/race-calendar"
                element={
                  <ProtectedAdminRoute>
                    <AdminRaceCalendar />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/market"
                element={
                  <ProtectedAdminRoute>
                    <AdminRunnerMarket />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/training-plan-journey"
                element={
                  <ProtectedAdminRoute>
                    <AdminTrainingJourney />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/site-content"
                element={<Navigate to="/admin/website" replace />}
              />
              <Route
                path="/admin/terms"
                element={<Navigate to="/admin/website?tab=terms" replace />}
              />
              <Route
                path="/admin/ai-knowledge"
                element={<Navigate to="/admin/website?tab=ai-kb" replace />}
              />
              <Route
                path="/admin/location-images"
                element={<Navigate to="/admin/website?tab=locations" replace />}
              />
              <Route
                path="/admin/email-settings"
                element={<Navigate to="/admin/website?tab=email" replace />}
              />
              <Route
                path="/admin/payment-settings"
                element={<Navigate to="/admin/website?tab=payment" replace />}
              />
              <Route
                path="/private-enquiries"
                element={<Navigate to="/admin/classes?tab=enquiries" replace />}
              />
              <Route
                path="/find-class-submissions"
                element={
                  <Navigate to="/admin/students?tab=enquiries" replace />
                }
              />
              <Route
                path="/payments"
                element={<Navigate to="/my-payments" replace />}
              />
              <Route
                path="/reports"
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route
                path="/attendance"
                element={
                  <ProtectedRoute allowedRoles={["admin", "coach"]}>
                    <MarkAttendance />
                  </ProtectedRoute>
                }
              />

              {/* Coach Routes */}
              <Route
                path="/coach/private-bookings"
                element={
                  <ProtectedRoute allowedRoles={["coach"]}>
                    <CoachPrivateBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mall-long-run"
                element={
                  <ProtectedAdminRoute>
                    <MallLongRunAdmin />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/whatsapp-reminders"
                element={
                  <ProtectedAdminRoute>
                    <AdminWhatsAppReminders />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/coach/technique-assessments"
                element={
                  <ProtectedRoute allowedRoles={["coach"]}>
                    <AdminTechniqueAssessments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-classes"
                element={<Navigate to="/coach/my-classes" replace />}
              />
              <Route
                path="/coach/profile"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <MyCoachProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/availability"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <MyCoachAvailability />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/my-classes"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <MyClasses />
                  </ProtectedRoute>
                }
              />
              {/* Msg 226 (Feb 2026) — Head Coach / Admin coach management inside the Coach Portal */}
              <Route
                path="/coach/coaches"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <MyCoachesInPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/coach-migration"
                element={
                  <ProtectedAdminRoute>
                    <AdminCoachMigration />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/ai-form-check-kb"
                element={
                  <ProtectedRoute allowedRoles={["admin", "coach"]}>
                    <AdminAIFormCheckKB />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/classes/:classId"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <CoachClassDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/classes/:classId/attendance"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <CoachMarkAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/scan"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <CoachScanCheckin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/checkin"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]} allowSharedCheckin>
                    <CoachCheckinPortal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checkin/:token"
                element={
                  <ProtectedRoute allowedRoles={["coach", "admin"]}>
                    <CoachScanCheckin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mark-attendance"
                element={<Navigate to="/coach/my-classes" replace />}
              />

              {/* Student/Parent Routes */}
              <Route path="/schedule" element={<Schedule />} />
              <Route
                path="/my-bookings"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <MyBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-races"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <MyRaces />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/family"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <FamilyProfiles />
                  </ProtectedRoute>
                }
              />
              {/* Packages browsing — public, no login required. Feb 9 2026: gate
                was removed so visitors can view pricing, credit tables, and
                comparisons before signing in. Login/guest is requested at
                Purchase click via the sheet inside BuyPackages.js. */}
              <Route path="/buy-packages" element={<BuyPackages />} />
              <Route path="/trials" element={<Trials />} />
              <Route path="/walk-in" element={<WalkInClasses />} />
              <Route path="/walk-in-classes" element={<WalkInClasses />} />
              <Route path="/packages" element={<BuyPackages />} />
              <Route path="/private-booking" element={<PrivateBooking />} />
              {/* Public booking confirmation — Feb 2026: no login gate. Refresh
                and browser Back stay on the public site. Both legacy and new
                URLs render the same component. */}
              <Route path="/payment-result" element={<PaymentResult />} />
              <Route
                path="/booking-confirmation/:orderId"
                element={<PaymentResult />}
              />
              <Route
                path="/booking-confirmation/booking/:bookingId"
                element={<BookingConfirmation />}
              />
              {/* Manual payment flow — public routes, no login gate. */}
              <Route path="/manual-payment/:mpId" element={<ManualPayment />} />
              <Route path="/price-lock" element={<PriceLockOffers />} />
              <Route
                path="/price-lock/offer/:offerId"
                element={
                  <ProtectedRoute>
                    <PriceLockReserve />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-reservations"
                element={
                  <ProtectedRoute>
                    <MyReservations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-submitted/:mpId"
                element={<PaymentSubmitted />}
              />
              <Route
                path="/summary/:token"
                element={<BookingSummaryPublic />}
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-payments"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <MyPayments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-wallet"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <MyWallet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-bookings/private/:id/assessment"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <PrivateAssessment />
                  </ProtectedRoute>
                }
              />

              {/* Default Redirect — landing is static, no Suspense wait. */}
              <Route path="/" element={<PublicLanding />} />
              <Route path="/pay/balance/:token" element={<BalancePayment />} />
              <Route path="/all-classes" element={<AllClasses />} />
              <Route path="/upcomingclass" element={<UpcomingClasses />} />
              <Route path="/events/paradigm-mall-long-run" element={<MallLongRun />} />
              <Route path="/events/paradigm-mall-long-run/registration/:ref" element={<MallLongRunRegistration />} />
              <Route path="/events/:slug" element={<EventClassPage />} />
              <Route path="/partner/login" element={<SupplierLogin mode="login" />} />
              <Route path="/partner/set-password" element={<SupplierLogin mode="set" />} />
              <Route path="/partner" element={<SupplierDashboard />} />
              <Route path="/partner/events/:slug" element={<SupplierEvent />} />
              <Route path="/partner/activities/:aid" element={<SupplierEvent />} />
              <Route path="/partner/stock" element={<SupplierStock view="stock" />} />
              <Route path="/partner/requests" element={<SupplierStock view="requests" />} />
              <Route path="/supplier/*" element={<PrefixRedirect from="/supplier" to="/partner" />} />
              <Route path="/admin/suppliers" element={<ProtectedAdminRoute><AdminSuppliers /></ProtectedAdminRoute>} />
              <Route path="/admin/suppliers/:id/:tab?" element={<ProtectedAdminRoute><AdminSupplierDetail /></ProtectedAdminRoute>} />
              <Route
                path="/admin/event-classes"
                element={
                  <ProtectedAdminRoute>
                    <AdminEventClasses />
                  </ProtectedAdminRoute>
                }
              />
              <Route path="/admin/event-classes/paradigm-mall-long-run/*" element={<Navigate to="/admin/mall-long-run" replace />} />
              <Route
                path="/admin/event-classes/:slug/:tab?"
                element={
                  <ProtectedAdminRoute>
                    <AdminEventManage />
                  </ProtectedAdminRoute>
                }
              />
              <Route path="/race-calendar" element={<RaceCalendar />} />
              <Route path="/find-my-race" element={<FindMyRace />} />
              <Route path="/runner-market" element={<RunnerMarket />} />
              <Route path="/runner-market/product/:id" element={<RunnerMarketProduct />} />
              <Route path="/runner-market/cart" element={<RunnerMarketCart />} />
              <Route path="/runner-market/checkout" element={<RunnerMarketCheckout />} />
              <Route path="/runner-market/find-order" element={<RunnerMarketFindOrder />} />
              <Route path="/runner-market/order/:orderNumber" element={<RunnerMarketOrder />} />
              <Route path="/class/:id" element={<ClassDetail />} />
              <Route path="/guest-checkout" element={<GuestCheckout />} />
              <Route path="/guest/confirm" element={<GuestConfirm />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/booking-help" element={<BookingHelp />} />
              <Route
                path="/policies/cancellation-refund"
                element={<CancellationRefundPolicy />}
              />
              <Route
                path="/policies/booking-rules"
                element={<BookingRulesPolicy />}
              />
              <Route
                path="/my-membership"
                element={
                  <ProtectedRoute allowedRoles={["student", "parent"]}>
                    <MyMembership />
                  </ProtectedRoute>
                }
              />
              <Route path="/info/:topic" element={<StudentInfo />} />
              <Route path="/info" element={<StudentInfo />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route
                path="/complete-registration"
                element={
                  <ProtectedRoute>
                    <CompleteRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              {/* Waitlist confirm — target of the offer email link */}
              <Route
                path="/waitlist/confirm/:token"
                element={<WaitlistConfirm />}
              />
              {/* Legal pages — public, no login required */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              {/* Private Coaching Hours packages — public browse & buy */}
              <Route
                path="/private-coaching-packages"
                element={<PrivateCoachingPackages />}
              />
              <Route path="/link-account" element={<LinkAccount />} />
              <Route
                path="/private-bookings/:bookingId/change"
                element={
                  <ProtectedRoute allowedRoles={["student"]}>
                    <ChangeCoachRequest />
                  </ProtectedRoute>
                }
              />
              <Route path="/private" element={<PrivateClass />} />
              <Route
                path="/online-training-plan"
                element={<OnlineTrainingPlan />}
              />
              <Route
                path="/training-plan/reservation/:sid"
                element={<TrainingPlanReservation />}
              />
              <Route
                path="/running-report/:aid"
                element={<RunningReportPage />}
              />
              <Route
                path="/ai-running-form-check"
                element={<AIFormCheckGate><AIRunningFormCheck /></AIFormCheckGate>}
              />
              <Route
                path="/ai-form-check/video"
                element={<AIFormCheckGate><AIFormCheckVideo /></AIFormCheckGate>}
              />
              <Route
                path="/ai-form-check/video/:id"
                element={<AIFormCheckGate><AIFormCheckVideo /></AIFormCheckGate>}
              />
              <Route
                path="/personal-coach-video-review"
                element={<AIFormCheckGate><PersonalCoachVideoReview /></AIFormCheckGate>}
              />
              <Route
                path="/ai-form-check/session/:id"
                element={<AIFormCheckGate><AIFormCheckSession /></AIFormCheckGate>}
              />
              <Route
                path="/admin/ai-form-checks/test"
                element={<AdminAIFormCheckTest />}
              />
              <Route
                path="/admin/ai-form-checks"
                element={
                  <ProtectedRoute allowedRoles={["admin", "coach"]}>
                    <AdminAIFormCheckQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ai-form-check/settings"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminAIFormCheckSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-ai-form-checks"
                element={
                  <ProtectedRoute>
                    <AIFormCheckGate><MyAIFormChecks /></AIFormCheckGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/technique-assessment" element={<TechniqueAssessment />} />
              <Route path="/technique-assessment/status/:id" element={<TechniqueAssessmentStatus />} />
              <Route
                path="/my-technique-assessments"
                element={
                  <ProtectedRoute>
                    <MyTechniqueAssessments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/technique-assessments"
                element={
                  <ProtectedAdminRoute>
                    <AdminTechniqueAssessments />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/ai-image-studio"
                element={
                  <ProtectedRoute>
                    <AIImageStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-promotions"
                element={
                  <ProtectedRoute>
                    <MyPromotions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ai-image-studio"
                element={
                  <ProtectedRoute allowedRoles={["admin", "coach"]}>
                    <AdminAIImageStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bulk-import"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminBulkImport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/test-user-cleanup"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminTestUserCleanup />
                  </ProtectedRoute>
                }
              />
              <Route path="/ai-mascot-preview" element={<MascotConcepts />} />
              <Route
                path="/compare-training-options"
                element={<CompareTrainingOptions />}
              />
              <Route path="/workshops" element={<Workshops />} />
              <Route path="/runners-stories" element={<RunnersStories />} />
              <Route path="/find-my-class" element={<FindMyClass />} />
              <Route path="/find-my-first-class" element={<FindMyFirstClass />} />
              <Route path="/book/:classId" element={<BookClass />} />
              <Route path="/book/confirmation/:orderNumber" element={<BookClassConfirmation />} />
              <Route path="/coaches" element={<Coaches />} />
              <Route
                path="/coaches/:coachIdOrSlug"
                element={<CoachProfile />}
              />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/running-classes" element={<ServicePage slug="running-classes" />} />
              <Route path="/running-classes/kids-teens" element={<ServicePage slug="kids-teens" />} />
              <Route path="/running-classes/adults" element={<ServicePage slug="adults" />} />
              <Route path="/running-classes/beginners" element={<ServicePage slug="beginners" />} />
              <Route path="/coaching" element={<ServicePage slug="coaching" />} />
              <Route path="/coaching/private" element={<ServicePage slug="private" />} />
              <Route path="/coaching/running-technique" element={<ServicePage slug="running-technique" />} />
              <Route path="/coaching/race-preparation" element={<ServicePage slug="race-preparation" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <RouteSeoDefaults />
          </Suspense>
          </RegistrationGate>
          {/* AskLGRAWidget is deferred behind Suspense so it never blocks the
            initial paint. fallback={null} keeps the DOM clean until ready. */}
          <Suspense fallback={null}>
            <AskLGRAWidget />
          </Suspense>
          {/* Mobile bottom nav — only renders for signed-in customers on
            mobile (component self-gates via useAuth + md:hidden). */}
          <Suspense fallback={null}>
            <MobileBottomNav />
          </Suspense>
          {/* Post-payment Return-To-Booking banner — self-gates via
            sessionStorage flag primed by /private-coaching-packages when
            entered with ?return_to=private-booking. Never shows on
            outbound legs (/checkout, /private-coaching-packages) or after
            dismissal. */}
          <Suspense fallback={null}>
            <ReturnToBookingBanner />
          </Suspense>
          <Toaster position="top-right" />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
